import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';

import { fetchMenuPulls, fetchMenuPushes } from './api';
import { Intent, LabeledSyncIntent, MenuSyncState } from './types';

export const isIntentScheduled = (scheduled_at: string) => {
  return DateTime.utc().toMillis() < DateTime.fromISO(scheduled_at).toUTC().toMillis();
};

const getLatestIntent = (pulls: Intent[], pushes: Intent[]): LabeledSyncIntent | null => {
  const latestPull = pulls.length > 0 ? pulls[0] : null;
  const latestPush = pushes.length > 0 ? pushes[0] : null;

  if (!latestPull && !latestPush) {
    return null;
  }

  if (!latestPull && latestPush) {
    return { intentType: 'push', ...latestPush };
  }

  if (!latestPush && latestPull) {
    return { intentType: 'pull', ...latestPull };
  }

  if (!latestPush || !latestPull) {
    return null;
  }

  if (new Date(latestPull.updated_at) > new Date(latestPush.updated_at)) {
    return { intentType: 'pull', ...latestPull };
  } else {
    return { intentType: 'push', ...latestPush };
  }
};

const getUnresolvedSyncStatusFromLatestIntent = (latestIntent: LabeledSyncIntent): MenuSyncState => {
  if (latestIntent.intentType === 'pull') {
    if (latestIntent.state === 'started') {
      return { status: 'pull-in-progress', latestIntent };
    }

    const status = isIntentScheduled(latestIntent.scheduled_at) ? 'pull-scheduled' : 'pull-in-progress';

    return { status, latestIntent };
  } else {
    {
      if (latestIntent.state === 'started') {
        return { status: 'push-in-progress', latestIntent };
      }

      const status = isIntentScheduled(latestIntent.scheduled_at) ? 'push-scheduled' : 'push-in-progress';

      return { status, latestIntent };
    }
  }
};

const getResolvedSyncStatusFromLatestIntent = (latestIntent: LabeledSyncIntent): MenuSyncState => {
  if (latestIntent.intentType === 'pull' && latestIntent.state === 'success') {
    return { status: 'pull-success', latestIntent };
  }

  if (latestIntent.intentType === 'pull' && latestIntent.state === 'failure') {
    return { status: 'pull-failure', latestIntent };
  }

  if (latestIntent.intentType === 'push' && latestIntent.state === 'success') {
    return { status: 'push-success', latestIntent };
  }

  if (latestIntent.intentType === 'push' && latestIntent.state === 'failure') {
    return { status: 'push-failure', latestIntent };
  }

  return { status: 'idle' };
};

export const useMenuSyncStatus = (menuId: string) => {
  const {
    data: syncStatus,
    isLoading: isLoadingSyncStatus,
    refetch: refetchSyncInfo,
  } = useQuery<MenuSyncState>(
    ['menu-sync-info', menuId],
    async () => {
      const latestUnresolvedPushIntent = await fetchMenuPushes(menuId, null, ['pending', 'started'], 1);
      const latestUnresolvedPullIntent = await fetchMenuPulls(menuId, null, ['pending', 'started'], 1);
      const latestUnresolvedIntent = getLatestIntent(
        latestUnresolvedPullIntent.pull_intents,
        latestUnresolvedPushIntent.push_intents,
      );

      if (latestUnresolvedIntent) {
        return getUnresolvedSyncStatusFromLatestIntent(latestUnresolvedIntent);
      } else {
        const lastResolvedPull = await fetchMenuPulls(menuId, null, ['success', 'failure'], 1);
        const lastResolvedPush = await fetchMenuPushes(menuId, null, ['success', 'failure'], 1);
        const latestIntent = getLatestIntent(lastResolvedPull.pull_intents, lastResolvedPush.push_intents);
        if (latestIntent) {
          const syncStatusFromLatestIntent = getResolvedSyncStatusFromLatestIntent(latestIntent);
          return syncStatusFromLatestIntent;
        }
      }

      return { status: 'idle' };
    },
    {
      refetchInterval: 60000,
      retry: false,
    },
  );

  return { syncStatus, isLoadingSyncStatus, refetchSyncInfo };
};
